import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { View } from 'react-native';
import { FadeInDown, FadeOutDown } from 'react-native-reanimated';
import { Portal } from '@rn-primitives/portal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { RandomUtils } from '@fytness/shared';
import { NativeOnlyAnimatedView } from '@/components/ui/native-only-animated-view';
import { Toast, type ToastVariant } from '@/components/ui/toast';
import { useContext } from '@/hooks/useContext';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Auto-dismiss delay in ms. Defaults to 3500. Pass 0 to persist. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: string;
}

interface ToastContextType {
  /** Show a toast; returns its id. */
  toast: (options: ToastOptions) => string;
  /** Dismiss a toast by id. */
  dismiss: (id: string) => void;
}

const DEFAULT_DURATION = 3500;

const ToastContext = createContext<ToastContextType | undefined>(undefined);
ToastContext.displayName = 'ToastContext';

export const useToast = () => useContext(ToastContext);

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * App-wide toast/notification system. Toasts stack at the bottom of the screen
 * (above everything, via the root `PortalHost`) and auto-dismiss. Mirrors the
 * imperative shape of `ModalContext`.
 */
export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const insets = useSafeAreaInsets();
  const { t } = useTranslation('common');
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = RandomUtils.uuid();
      setToasts((prev) => [...prev, { ...options, id }]);

      const duration = options.duration ?? DEFAULT_DURATION;
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  // Clear any pending timers on unmount.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <Portal name="toast-portal">
        <View
          pointerEvents="box-none"
          className="absolute inset-x-0 bottom-0 items-center gap-2 px-4"
          style={{ paddingBottom: insets.bottom + 16 }}
        >
          {toasts.map((item) => (
            <NativeOnlyAnimatedView
              key={item.id}
              entering={FadeInDown}
              exiting={FadeOutDown}
              className="w-full items-center"
            >
              <Toast
                variant={item.variant}
                title={item.title}
                description={item.description}
                dismissLabel={t('toast.dismiss')}
                onDismiss={() => dismiss(item.id)}
              />
            </NativeOnlyAnimatedView>
          ))}
        </View>
      </Portal>
    </ToastContext.Provider>
  );
};
